import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Bookmark, Check, Download, Gift, Mail, Sparkles, Headphones, Calendar as CalIcon, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// MailerLite Group ID for this lead magnet ("Tap-in 12 month Calendar")
const MAILERLITE_GROUP_ID = "186110662635684873";

// Combined 12-month printable PDF (also served from MailerLite emails)
const CALENDAR_PDF_URL = "/downloads/I-GET-TO-Calendar-2026.pdf";
const CALENDAR_PDF_FILENAME = "I-GET-TO-Calendar-2026.pdf";

const CALENDARS = [
  { src: "/calendar-2.png",  label: "January",   theme: "New Beginnings" },
  { src: "/calendar-3.png",  label: "February",  theme: "Self-Love" },
  { src: "/calendar-4.png",  label: "March",     theme: "Renewal" },
  { src: "/calendar-5.png",  label: "April",     theme: "Mind Body Spirit" },
  { src: "/calendar-6.png",  label: "May",       theme: "Sacred Home" },
  { src: "/calendar-7.png",  label: "June",      theme: "Inner Wisdom" },
  { src: "/calendar-1.png",  label: "July",      theme: "Release & Flow" },
  { src: "/calendar-9.png",  label: "August",    theme: "Authentic Self" },
  { src: "/calendar-10.png", label: "September", theme: "Intentional Living" },
  { src: "/calendar-11.png", label: "October",   theme: "Mindful Thought" },
  { src: "/calendar-12.png", label: "November",  theme: "Gratitude" },
  { src: "/calendar-13.png", label: "December",  theme: "Wholeness" },
];

const STORAGE_KEY = "tap-in-calendar-unlocked";

export default function TapInCalendar() {
  const [searchParams] = useSearchParams();
  const [unlocked, setUnlocked] = useState(false);
  const [formData, setFormData] = useState({ firstName: "", email: "" });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const desc = "Free printable 12-Month I GET TO Calendar from Temple Franklin. 365 daily reframes. Pairs with the Tap-In HYGIENE practice. Weekly walk-with-me emails included.";
    const url = "https://mindbodyspirithygienetools.com/tap-in-calendar";

    const existingLd = document.getElementById("calendar-jsonld");
    if (existingLd) existingLd.remove();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "calendar-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: "12-Month I GET TO Calendar",
      description: desc,
      url,
      brand: { "@type": "Brand", name: "Mind Body Spirit HYGIENE" },
      offers: { "@type": "Offer", price: "0.00", priceCurrency: "USD", availability: "https://schema.org/InStock" },
    });
    document.head.appendChild(ld);

    try {
      // Auto-unlock if:
      // (a) URL has ?unlocked=1, ?u=1, or ?from=email — used in MailerLite email button links
      //     so subscribers who click the calendar email on a fresh device skip the gate
      // (b) Prior device-level unlock exists in localStorage
      const bypassParam =
        searchParams.get("unlocked") === "1" ||
        searchParams.get("u") === "1" ||
        searchParams.get("from") === "email";
      if (bypassParam) {
        setUnlocked(true);
        try { localStorage.setItem(STORAGE_KEY, "true"); } catch (_) { /* ignore */ }
      } else if (localStorage.getItem(STORAGE_KEY) === "true") {
        setUnlocked(true);
      }
    } catch (e) {
      // ignore
    }
    return () => {
      const ld2 = document.getElementById("calendar-jsonld");
      if (ld2) ld2.remove();
    };
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });
    try {
      await axios.post(`${API}/subscribe`, {
        firstName: formData.firstName,
        email: formData.email,
        group_id: MAILERLITE_GROUP_ID,
        source: "tap-in-calendar-lead-magnet",
      });
      try { localStorage.setItem(STORAGE_KEY, "true"); } catch (err) { /* ignore */ }
      setUnlocked(true);
      setStatus({ type: "success", message: "🎉 Your calendar is unlocked — scroll down to download every month." });
    } catch (err) {
      setStatus({ type: "error", message: "Something went wrong. Try again or refresh." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="tap-in-calendar-page">
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-[#25a1c2] hover:text-[#042F2E] text-sm font-medium inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/build-your-own" className="text-[#25a1c2] hover:text-[#042F2E] text-xs font-bold" data-testid="calendar-nav-build-your-own">Build Your Own</Link>
            <Link to="/blog/the-tap-in" className="text-[#25a1c2] hover:text-[#042F2E] text-xs font-bold inline-flex items-center gap-1.5">
              <Headphones className="w-3.5 h-3.5" /> Audio Essay
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-32 pb-10 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#25a1c2] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-5">
            <Gift className="w-3 h-3" /> Free · No Credit Card · Printable
          </div>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tighter text-[#042F2E] leading-[0.95] mb-5" data-testid="page-title">
            The 12-Month<br /><em className="text-[#25a1c2]">I GET TO</em> Calendar
          </h1>
          <p className="text-lg md:text-xl text-[#475569] leading-relaxed max-w-2xl mx-auto mb-4">
            The visual anchor for your Tap-In HYGIENE practice. <strong className="text-[#042F2E]">365 daily "I GET TO" prompts.</strong> Print it. Put it where your eyes land. Walk with me week by week.
          </p>
          <p className="text-sm text-[#475569] italic">
            Plus — my weekly walk-with-me emails. One short note, one reframe, one breath. 💛
          </p>
        </div>
      </section>

      {/* LEAD MAGNET GATE (hidden if unlocked) */}
      {!unlocked && (
        <section className="pb-16 px-6" data-testid="lead-magnet-gate">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#042F2E] via-[#25a1c2] to-[#068fa2] rounded-3xl p-8 md:p-12 shadow-2xl">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#5EEAD4] text-[#042F2E] rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest mb-3">
                  <Sparkles className="w-3 h-3" /> Unlock all 12 pages
                </div>
                <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight mb-4">
                  Enter your name &amp; email — I'll send you home.
                </h2>
                <ul className="space-y-2 mb-2">
                  {[
                    "All 12 printable pages (one for every month)",
                    "Weekly walk-with-me emails — 1 short note",
                    "Free Yes-No-Wait cards added on Day 30",
                    "Unsubscribe anytime — no spam, ever",
                  ].map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                      <Check className="w-4 h-4 text-[#5EEAD4] shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl p-5 md:p-6 shadow-xl space-y-3"
                data-testid="lead-magnet-form"
              >
                <input
                  type="text"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-[#CCFBF1] rounded-xl focus:border-[#25a1c2] focus:ring-2 focus:ring-[#25a1c2]/20 outline-none text-sm text-[#042F2E]"
                  data-testid="input-firstname"
                />
                <div className="relative">
                  <Mail className="w-4 h-4 text-[#475569] absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full pl-11 pr-4 py-3 border border-[#CCFBF1] rounded-xl focus:border-[#25a1c2] focus:ring-2 focus:ring-[#25a1c2]/20 outline-none text-sm text-[#042F2E]"
                    data-testid="input-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#042F2E] hover:bg-[#068fa2] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-xl transition-all inline-flex items-center justify-center gap-2"
                  data-testid="submit-btn"
                >
                  {isSubmitting ? "Sending home…" : <>Send Me the Calendar <Gift className="w-4 h-4" /></>}
                </button>
                {status.message && (
                  <p className={`text-center text-xs font-bold ${status.type === "error" ? "text-red-600" : "text-[#25a1c2]"}`}>
                    {status.message}
                  </p>
                )}
                <p className="text-[10px] text-[#475569] text-center italic">
                  By subscribing, you agree to receive weekly emails from Temple. Welcome home. 💛
                </p>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* UNLOCKED — Downloads */}
      {unlocked && (
        <section className="pb-16 px-6" data-testid="unlocked-section">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-[#F0FDF4] to-[#CCFBF1] border-2 border-[#25a1c2] rounded-3xl p-6 md:p-8 text-center mb-6">
              <Check className="w-10 h-10 text-[#25a1c2] mx-auto mb-3" />
              <h2 className="font-serif text-3xl text-[#042F2E] mb-2">Welcome home, dear one. 💛</h2>
              <p className="text-[#475569] text-sm max-w-md mx-auto mb-6">
                One button below gets you the whole thing — all 12 months as a single printable PDF.
              </p>
              <a
                href={CALENDAR_PDF_URL}
                download={CALENDAR_PDF_FILENAME}
                className="inline-flex items-center gap-3 bg-[#042F2E] hover:bg-[#068fa2] text-white font-bold rounded-full px-8 py-4 text-base transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                data-testid="download-full-pdf-btn"
                onClick={() => {
                  if (typeof window !== "undefined" && typeof window.gtag === "function") {
                    window.gtag("event", "download_calendar_pdf", { source: "tap-in-calendar" });
                  }
                }}
              >
                <FileText className="w-5 h-5" />
                Download the full calendar (PDF · all 12 months)
                <Download className="w-4 h-4" />
              </a>
              <p className="text-xs text-[#475569] italic mt-4">
                Prefer individual months? They're all below — right-click any page to save.
              </p>
            </div>

            {/* THREE BEATS · Trust + return-path */}
            <div className="bg-[#042F2E] text-white rounded-3xl p-6 md:p-8 mb-10" data-testid="three-beats">
              <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#5EEAD4] font-bold mb-4 text-center">Three small things, dear one</p>
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div data-testid="beat-save">
                  <Download className="w-5 h-5 text-[#5EEAD4] mb-2" />
                  <p className="font-serif text-lg mb-1.5 text-white">1. Save now</p>
                  <p className="text-white/75 leading-relaxed">
                    Every page is yours below. Right-click → <em>Save Image As...</em> on desktop. Long-press → <em>Save to Photos</em> on phone. Do it now, while you're here.
                  </p>
                </div>
                <div data-testid="beat-bookmark">
                  <Bookmark className="w-5 h-5 text-[#5EEAD4] mb-2" />
                  <p className="font-serif text-lg mb-1.5 text-white">2. Bookmark this page</p>
                  <p className="text-white/75 leading-relaxed">
                    Press <kbd className="bg-white/10 border border-white/20 rounded px-1.5 py-0.5 text-[11px] font-sans">⌘+D</kbd> on Mac or <kbd className="bg-white/10 border border-white/20 rounded px-1.5 py-0.5 text-[11px] font-sans">Ctrl+D</kbd> on Windows. Your calendar lives here, always. Come back anytime.
                  </p>
                </div>
                <div data-testid="beat-inbox">
                  <Mail className="w-5 h-5 text-[#5EEAD4] mb-2" />
                  <p className="font-serif text-lg mb-1.5 text-white">3. Watch your inbox</p>
                  <p className="text-white/75 leading-relaxed">
                    Short walk-with-me notes will arrive from <strong className="text-white break-all">temple@mindbodyspirithygienetools.com</strong>. Add me to your contacts so I land in your inbox, not spam. 💛
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="calendar-downloads">
              {CALENDARS.map((cal, i) => (
                <a
                  key={cal.src}
                  href={cal.src}
                  download
                  className="group bg-white rounded-xl border border-[#CCFBF1] overflow-hidden hover:shadow-lg hover:border-[#25a1c2] transition-all"
                  data-testid={`download-${cal.label.toLowerCase()}`}
                >
                  <div className="aspect-[3/4] bg-[#F0F5F4] overflow-hidden">
                    <img src={cal.src} alt={cal.label} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="px-3 py-3 text-center">
                    <p className="font-serif text-base text-[#042F2E] font-bold">{cal.label}</p>
                    <p className="font-sans text-[10px] text-[#475569] italic mt-0.5">{cal.theme}</p>
                    <p className="inline-flex items-center gap-1 text-[10px] text-[#25a1c2] font-bold mt-1 group-hover:underline">
                      <Download className="w-3 h-3" /> Download
                    </p>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-12 bg-white rounded-2xl border border-[#CCFBF1] p-6 md:p-8 text-center">
              <Headphones className="w-8 h-8 text-[#25a1c2] mx-auto mb-3" />
              <h3 className="font-serif text-2xl text-[#042F2E] mb-2">Haven't heard the practice yet?</h3>
              <p className="text-[#475569] text-sm mb-5 max-w-md mx-auto">
                The 9-minute audio essay that started all of this. Real voice. The HYGIENE that reclaimed my life.
              </p>
              <Link
                to="/blog/the-tap-in"
                className="inline-flex items-center gap-2 bg-[#042F2E] hover:bg-[#068fa2] text-white font-bold text-sm rounded-full px-6 py-3 transition-all"
              >
                <Headphones className="w-4 h-4" /> Listen to The Tap-In
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* WHAT'S INSIDE (always visible) */}
      {!unlocked && (
        <section className="pb-16 px-6">
          <div className="max-w-5xl mx-auto">
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#25a1c2] font-bold mb-3 text-center">A peek inside</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#042F2E] text-center mb-8">What's in your calendar</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 opacity-70 blur-[1px]" aria-hidden="true">
              {CALENDARS.slice(0, 8).map((cal) => (
                <div key={cal.src} className="bg-white rounded-xl border border-[#CCFBF1] overflow-hidden">
                  <div className="aspect-[3/4] bg-[#F0F5F4] overflow-hidden">
                    <img src={cal.src} alt={`I GET TO calendar page — ${cal.label || cal.month || "month preview"}`} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="px-3 py-2 text-center">
                    <p className="font-sans text-xs font-bold text-[#042F2E]">{cal.label}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-[#475569] italic mt-5">
              <CalIcon className="w-3 h-3 inline-block mr-1 -mt-0.5" /> 12 printable pages · Unlock above to download
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
